import React from 'react';
import {Box, Typography} from '@mui/material';
import ProblemShifts from './ProblemShifts';
import Shifts from './Shifts';
import {PaperWrapper} from '../atoms/PaperWrapper/PaperWrapper';
import HoursColumn, {HoursComparison} from './HoursColumn';

const overScheduleColour = '#b53d47';
const money = cost => `${cost.currencySymbol || ''}${cost.amount.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}${cost.currencySymbol ? '' : ' (currency unspecified)'}`;

export default function HoursReport({row, siteName, title, provisional, period = provisional ? 'current' : 'past', canSeeCosts, all = false}) {
  const progress = row.hoursProgress;
  const punchEligibleShifts = typeof row.punchEligibleShifts === 'number' ? row.punchEligibleShifts : null;
  const missedPunches = punchEligibleShifts != null && typeof row.missedPunchShifts === 'number' ? row.missedPunchShifts : null;
  const missedPunchPercent = missedPunches != null && punchEligibleShifts > 0
    ? (missedPunches / punchEligibleShifts * 100).toLocaleString('en-GB', {maximumFractionDigits: 1}) : null;
  const projected = progress?.projectedHours ?? row.latestHours;
  const missingProjectionShifts = progress?.missingProjectionShifts ?? row.missingActualShifts ?? 0;
  const estimatedShifts = progress?.estimatedShifts ?? row.scheduledFallbackShifts ?? 0;
  const incomplete = !progress || row.invalidScheduleShifts > 0 || missingProjectionShifts > 0;
  const difference = !incomplete && projected != null && row.shiftCount ? projected - row.scheduledHours : null;
  const over = difference != null && difference > 0.05;
  const under = difference != null && difference < -0.05;
  const varianceLabel = over ? 'Over Schedule' : under ? 'Below Schedule' : difference == null ? 'Schedule Variance' : 'On Schedule';
  // Shared scale leaves visible space beyond the weekly target for overruns.
  const hourly = row.payTypeHours?.hourly;
  const showHourly = hourly?.shiftCount > 0 && hourly.shiftCount < row.shiftCount;
  const totals = {shiftCount: row.shiftCount, scheduledHours: row.scheduledHours,
    currentHours: progress?.currentHours ?? progress?.recordedHours ?? null, projectedHours: projected,
    estimatedShifts, missingCurrentShifts: progress?.missingCurrentShifts || 0,
    invalidScheduleShifts: row.invalidScheduleShifts, missingProjectionShifts};
  const maximum = Math.max(1, row.scheduledHours, totals.currentHours || 0, projected || 0,
    showHourly ? hourly.scheduledHours : 0, showHourly ? hourly.currentHours || 0 : 0,
    showHourly ? hourly.projectedHours || 0 : 0) * 1.1;
  const problemRow = {...row, reportId: `${row.siteId}-${all ? 'all' : row.departmentId ?? 'unassigned'}`,
    name: all ? siteName : `${siteName} · ${row.name}`};
  return <Box component="section" aria-label={`${siteName} · ${title}`}
    sx={{minWidth: 0, height: '100%', '& > .MuiPaper-root': {boxSizing: 'border-box'}}}>
    <PaperWrapper>
    <Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap'}}>
      <Box><Typography component="h3" variant="h6">{title}</Typography>
        {all && <Typography variant="caption" color="text.secondary">All departments · {siteName}</Typography>}
      </Box>
    </Box>
    <Box sx={{display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 3, mt: 2, mb: 1}}>
      {period !== 'future' && <Box>
        <Typography variant="caption" color="text.secondary" sx={{textTransform:'uppercase'}}>{varianceLabel}</Typography>
        <HoursComparison totals={totals} />
      </Box>}
      <Box><Typography variant="caption" color="text.secondary" sx={{textTransform:'uppercase'}}>Punch Misses</Typography>
        <Typography component="p" variant="h5" title="Shifts dated before today (Europe/London) missing at least one punch time.">
          {missedPunches == null ? '—' : <>
            {missedPunches}/{punchEligibleShifts}
            {missedPunchPercent != null && <Box component="span"
              sx={{color: missedPunches / punchEligibleShifts > 0.1 ? overScheduleColour : 'inherit'}}>
              {' '}({missedPunchPercent}%)
            </Box>}
          </>}
        </Typography>
      </Box>
      {canSeeCosts && <Box><Typography variant="caption" color="text.secondary" sx={{textTransform:'uppercase'}}>Cost estimate</Typography>
        {row.costedShifts ? row.costs.map(cost => <Typography component="p" variant="h5" key={cost.currencySymbol}>{money(cost)}</Typography>) : <Typography component="p" variant="h5">—</Typography>}
      </Box>}
    </Box>
    <Box sx={{display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 4, mt: 3, mb:4}}>
      <HoursColumn title="All Staff" hideComparison totals={totals} period={period} maximum={maximum} />
      {showHourly && <HoursColumn title="Hourly-rate staff" hideComparison totals={hourly} period={period} maximum={maximum} />}
    </Box>
    {hourly?.shiftCount > 0 && !showHourly && <Typography variant="caption" color="text.secondary">All staff are hourly paid.</Typography>}
    <Box sx={{mt: 1}}><Shifts site={problemRow} /></Box>
    <Box sx={{mt: 1}}><ProblemShifts site={problemRow} canSeeCosts={canSeeCosts} /></Box>
    </PaperWrapper>
  </Box>;
}
